import { Fragment, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as AntTooltip, Button } from 'antd';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { CustomTooltip } from './tooltip';
import { UnorderedListOutlined, PieChartOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';

// 100 cores fixas (hex)
const fixedColors = [
    '#1B3A7C', // Dark Blue
    '#4A90E2', // Blue
    '#F5A623', // Orange
    '#D622A9', // Magenta
    '#BD10E0', // Purple
    '#9013FE', // Violet
    '#50E3C2', // Aqua
    '#7ED321', // Green
    '#B8E986', // Light Green
    '#F8E71C', // Yellow
    '#F45B69', // Coral
    '#417505', // Olive Green
    '#FF7F50', // Coral Pink
    '#7F8C8D', // Neutral Gray
    '#3E4A59', // Slate Gray
    '#FF5733',
    '#C70039',
    '#900C3F',
    '#581845',
    '#FFC300',
    '#DAF7A6',
    '#1ABC9C',
    '#2ECC71',
    '#3498DB',
    '#9B59B6',
    '#34495E',
    '#16A085',
    '#27AE60',
    '#2980B9',
    '#8E44AD',
    '#2C3E50',
    '#F1C40F',
    '#E67E22',
    '#E74C3C',
    '#ECF0F1',
    '#95A5A6',
    '#F39C12',
    '#D35400',
    '#C0392B',
    '#BDC3C7',
    '#FF9999',
    '#FF6666',
    '#FF3333',
    '#FF0000',
    '#CC0000',
    '#990000',
    '#660000',
    '#330000',
    '#FFCC99',
    '#FF9966',
    '#FF6633',
    '#CC3300',
    '#993300',
    '#663300',
    '#FFCC00',
    '#FF9900',
    '#FF6600',
    '#CC6600',
    '#FFFF99',
    '#FFFF66',
    '#FFFF33',
    '#FFFF00',
    '#CCCC00',
    '#999900',
    '#666600',
    '#333300',
    '#99FF99',
    '#66FF66',
    '#33FF33',
    '#00FF00',
    '#00CC00',
    '#009900',
    '#006600',
    '#003300',
    '#99FFFF',
    '#66FFFF',
    '#33FFFF',
    '#00FFFF',
    '#00CCCC',
    '#009999',
    '#006666',
    '#003333',
    '#9999FF',
    '#6666FF',
    '#3333FF',
    '#0000FF',
    '#0000CC',
    '#000099',
    '#000066',
    '#000033',
    '#E6B0AA',
    '#D98880',
    '#C39BD3',
    '#7FB3D5',
    '#76D7C4',
    '#7DCEA0',
    '#F7DC6F',
    '#F0B27A',
    '#E59866',
    '#B22222'
];

const DonutChartWithDetails = memo(({ data = [], categories = [], onClick, activeFilters = [], disabled = false, columnsCount = 3, onRenderHeaderButton, onOthersClick }) => {
    const { t } = useTranslation();
    const isFourColumns = columnsCount >= 4; // Detecta quando hÃ¡ 4 ou mais colunas (card pequeno)

    // Calcula o total e prepara os dados originais
    const total = data.reduce((acc, val) => acc + val, 0);
    const originalData = data.map((value, index) => ({
        name: categories[index],
        value,
        percent: total > 0 ? (value / total) * 100 : 0,
    }));

    // Ordena os dados sempre do maior para o menor
    const sortedListData = [...originalData].sort((a, b) => b.value - a.value);

    // 1) constantes de threshold e max
    const PERCENT_THRESHOLD = 2; // <2% considera â€œpequenoâ€
    const MAX_SLICES = 7; // 6 maiores + OTHERS

    // 2) calcula quantidade total de itens para decidir se aplica agrupamento
    const totalItems = sortedListData.length;

    // 3) prepara estruturas para os dois cenÃ¡rios:
    //    - totalItems >= 7 -> aplica regra de <2% + OTHERS
    //    - totalItems < 7  -> nÃ£o agrupa nada, todos aparecem individualmente
    let smallSlices = [];
    let bigSlices = [];
    let visibleBig = [];
    let overflowBig = [];
    let othersGroup = [];
    let othersValue = 0;
    let othersPercent = 0;

    if (totalItems >= 7) {
        // separa sortedListData em grandes e pequenas
        smallSlices = sortedListData.filter(i => i.percent < PERCENT_THRESHOLD);
        bigSlices = sortedListData.filter(i => i.percent >= PERCENT_THRESHOLD);

        // pega sÃ³ as top (MAX_SLICES - 1) fatias grandes
        visibleBig = bigSlices.slice(0, MAX_SLICES - 1);
        overflowBig = bigSlices.slice(MAX_SLICES - 1); // restante vira Others

        // monta o grupo "OTHERS" com todas as pequenas + overflow
        othersGroup = [...smallSlices, ...overflowBig];
        othersValue = othersGroup.reduce((sum, i) => sum + i.value, 0);
        othersPercent = othersGroup.reduce((sum, i) => sum + (Number(i.percent) || 0), 0);
    } else {
        // atÃ© 6 itens: todos aparecem como fatias individuais, sem OTHERS
        bigSlices = [...sortedListData];
        visibleBig = [...sortedListData];
        overflowBig = [];
        othersGroup = [];
        othersValue = 0;
        othersPercent = 0;
    }
    const sortedOthersGroup = [...othersGroup].sort((a, b) => {
        if (b.value !== a.value) return b.value - a.value;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });

    // 4) monta chartData: visiveis + OTHERS (se houver)
    const chartData = [
        ...visibleBig.map(item => ({ ...item, displayName: item.name })),
        ...(totalItems >= 7 && othersGroup.length > 0
            ? [{ name: "OTHERS", displayName: t("Dashboard.Others"), value: othersValue, percent: othersPercent }]
            : othersGroup.map(item => ({ ...item, displayName: item.name })))
    ];

    // Mapeamento de cores utilizando as 100 cores fixas
    const categoryColors = {};
    chartData.forEach((entry, index) => {
        categoryColors[entry.name] = fixedColors[index % fixedColors.length];
    });
    if (chartData.some(item => item.name === "OTHERS")) {
        categoryColors["OTHERS"] = '#CCCCCC';
    }

    // Estados para categorias ativas, categoria em hover e controle da animaÃ§Ã£o
    const [activeCategories, setActiveCategories] = useState([]);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [showOthersItems, setShowOthersItems] = useState(false);

    useEffect(() => {
        if (JSON.stringify(activeFilters) !== JSON.stringify(activeCategories)) {
            setActiveCategories(activeFilters);
        }
    }, [activeFilters]);

    // Com 4 colunas (card pequeno), lista fica oculta por padrÃ£o, mas pode ser expandida
    const [showDetails, setShowDetails] = useState(!isFourColumns); // Lista visÃvel por padrÃ£o, exceto com 4 colunas
    
    // Reseta o estado quando o nÃºmero de colunas mudar
    useEffect(() => {
        setShowDetails(!isFourColumns);
    }, [isFourColumns]);

    // ExpÃµe o botÃ£o para o header do Card
    useEffect(() => {
        if (onRenderHeaderButton && isFourColumns) {
            onRenderHeaderButton(
                <Button
                    type="text"
                    icon={showDetails ? <PieChartOutlined /> : <UnorderedListOutlined />}
                    onClick={() => setShowDetails(!showDetails)}
                    style={{
                        color: showDetails ? '#4085c6' : '#8c8c8c',
                        padding: '4px 8px',
                        height: 'auto',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#4085c6';
                        e.currentTarget.style.backgroundColor = '#f0f7ff';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = showDetails ? '#4085c6' : '#8c8c8c';
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={showDetails ? t('Dashboard.ShowChart') : t('Dashboard.Details')}
                />
            );
        } else if (onRenderHeaderButton) {
            onRenderHeaderButton(null);
        }
    }, [showDetails, isFourColumns, onRenderHeaderButton, t]);

    const toggleSelection = (category) => {
        if (disabled) return;
        if (category === "OTHERS") return; // "OTHERS" nÃ£o Ã© interativo
        setActiveCategories((prev) => {
            const isActive = prev.includes(category);
            const newCategories = isActive ? prev.filter(c => c !== category) : [...prev, category];
            onClick && onClick(category);
            return newCategories;
        });
    };

    const onPieClick = (_, index) => {
        if (index >= 0 && index < chartData.length) {
            const clickedItem = chartData[index];
            if (clickedItem && clickedItem.name === "OTHERS") {
                setShowOthersItems(!showOthersItems);
                onOthersClick && onOthersClick();
            } else if (clickedItem && clickedItem.name !== "OTHERS") {
                toggleSelection(clickedItem.name);
            }
        }
    };

    const onListItemMouseEnter = (category) => {
        setHoveredCategory(category);
    };

    const onListItemMouseLeave = () => {
        setHoveredCategory(null);
    };

    const onListItemClick = (category, e) => {
        e.stopPropagation();
        if (category === "OTHERS") {
            setShowOthersItems(!showOthersItems);
            onOthersClick && onOthersClick();
        } else {
            toggleSelection(category);
        }
    };

    if (!data.length || !categories.length || data.length !== categories.length) {
        return <p>{t('Dashboard.Not')}</p>;
    }

    // Sempre mostra labels nos grÃ¡ficos
    const shouldShowLabels = true;
    
    // Ajusta tamanhos: com 4 colunas, faz swap: mostra 100% grÃ¡fico OU 100% lista (alterna)
    // Sem 4 colunas, mostra grÃ¡fico 50% + lista 50% lado a lado
    const chartWidth = isFourColumns ? (showDetails ? "0%" : "100%") : "50%";
    const detailsWidth = isFourColumns ? (showDetails ? "100%" : "0%") : "50%";
    const chartHeight = 250;
    const innerRadius = 55;
    const outerRadius = 85;
    
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                flexWrap: 'nowrap',
                gap: 12,
                position: 'relative',
                minHeight: chartHeight
            }}
        >
            {(!isFourColumns || !showDetails) && (
                <ResponsiveContainer 
                    width={chartWidth} 
                    height={chartHeight}
                    style={{
                        transition: 'width 0.3s ease, opacity 0.3s ease',
                        opacity: isFourColumns && showDetails ? 0 : 1
                    }}
                >
                <PieChart>
                    <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        minAngle={5}
                        paddingAngle={2}
                        isAnimationActive={!hasAnimated}
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                        onAnimationEnd={() => setHasAnimated(true)}
                        onClick={onPieClick}
                        onMouseEnter={(data, index, e) => {
                            if (data && data.payload) {
                                const category = data.payload.name;
                                if (category !== hoveredCategory) {
                                    onListItemMouseEnter(category);
                                }
                            }
                        }}
                        onMouseLeave={onListItemMouseLeave}
                        cursor={disabled ? "not-allowed" : "pointer"}
                        label={shouldShowLabels ? ({ name, value, percent, cx, cy, midAngle, outerRadius }) => {
                            const RADIAN = Math.PI / 180;
                            const radius = outerRadius + 30;
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            const percentValue = Number(percent) || 0;
                            return (
                                <text
                                    x={x}
                                    y={y}
                                    fill="#262626"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                    fontSize="11px"
                                    fontWeight="600"
                                >
                                    <tspan>{`${value} (${percentValue.toFixed(2)}%)`}</tspan>
                                </text>
                            );
                        } : false}
                    >
                        {chartData.map((entry, index) => {
                            const opacity = hoveredCategory
                                ? (entry.name === hoveredCategory ? 1 : 0.3)
                                : (activeCategories.length === 0 || activeCategories.includes(entry.name) ? 1 : 0.3);
                            const isHovered = hoveredCategory === entry.name;
                            const isActive = activeCategories.length === 0 || activeCategories.includes(entry.name);
                            const baseColor = entry.name === "OTHERS" ? '#CCCCCC' : categoryColors[entry.name];
                            
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={baseColor}
                                    opacity={opacity}
                                    style={entry.name === "OTHERS" ? { cursor: 'pointer' } : { cursor: 'pointer' }}
                                    stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                    strokeWidth={isHovered || isActive ? 3 : 0}
                                />
                            );
                        })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
            )}

            {(!isFourColumns || showDetails) && (
                <div
                    style={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        width: detailsWidth,
                        paddingLeft: 8,
                        paddingRight: 0,
                        transition: 'width 0.3s ease, opacity 0.3s ease',
                        opacity: isFourColumns && !showDetails ? 0 : 1,
                        animation: isFourColumns && showDetails ? 'fadeIn 0.3s ease' : 'none'
                    }}
                >
                    <h4 style={{ marginBottom: 10, fontSize: 14, fontWeight: 700, color: '#262626' }}>{t('Dashboard.Details')}</h4>
                <ul style={{ listStyle: 'none', padding: '2px 0 0 4px', margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {chartData.map((entry, index) => {
                        const percentValue = Number(entry.percent) || 0;
                        const percentFormatted = percentValue.toFixed(2);
                        const isOthers = entry.name === "OTHERS";
                        const isOthersExpanded = isOthers && showOthersItems;
                        
                        return (
                            <Fragment key={`list-item-${index}`}>
                                <AntTooltip title={`${entry.displayName || entry.name}: ${entry.value} (${percentFormatted}%)`}>
                                    <li
                                        onMouseEnter={() => !isOthers && onListItemMouseEnter(entry.name)}
                                        onMouseLeave={onListItemMouseLeave}
                                        onClick={(e) => onListItemClick(entry.name, e)}
                                        style={{
                                            padding: '2px 4px',
                                            cursor: 'pointer',
                                            backgroundColor: activeCategories.includes(entry.name) ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                            borderRadius: '6px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            flexWrap: 'nowrap',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible',
                                            textOverflow: 'clip',
                                            fontSize: 13,
                                            gap: 6,
                                            width: 'fit-content',
                                            maxWidth: '100%',
                                            transition: 'all 0.2s ease',
                                            border: activeCategories.includes(entry.name) ? '1px solid #4085c6' : '1px solid transparent',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', flex: '0 1 auto', minWidth: 0 }}>
                                            {isOthers && (
                                                <span style={{ marginRight: '4px', fontSize: '10px' }}>
                                                    {isOthersExpanded ? <UpOutlined /> : <DownOutlined />}
                                                </span>
                                            )}
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    backgroundColor: categoryColors[entry.name],
                                                    borderRadius: '2px',
                                                    marginRight: '4px',
                                                    flexShrink: 0,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }}
                                            ></span>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.displayName || entry.name}</span>
                                        </div>
                                        <strong style={{ marginLeft: 2, color: '#434343', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                            {entry.value} ({percentFormatted}%)
                                        </strong>
                                    </li>
                                </AntTooltip>
                                
                                {/* Lista expansÃvel de itens dentro de "OTHERS" */}
                                {isOthers && isOthersExpanded && othersGroup.length > 0 && (
                                    <li style={{ paddingLeft: '12px', marginTop: '4px', marginBottom: '4px' }}>
                                        <ul style={{ listStyle: 'none', padding: '2px 0 0 4px', margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {sortedOthersGroup.map((item, itemIndex) => {
                                                const itemPercent = Number(item.percent || 0);
                                                const isItemActive = activeCategories.includes(item.name);
                                                return (
                                                    <li
                                                        key={`others-item-${itemIndex}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSelection(item.name);
                                                        }}
                                                        onMouseEnter={() => onListItemMouseEnter(item.name)}
                                                        onMouseLeave={onListItemMouseLeave}
                                                        style={{
                                                            padding: '2px 4px',
                                                            cursor: 'pointer',
                                                            backgroundColor: isItemActive ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                                            borderRadius: '6px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-start',
                                                            flexWrap: 'nowrap',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'visible',
                                                            textOverflow: 'clip',
                                                            fontSize: 13,
                                                            gap: 6,
                                                            width: 'fit-content',
                                                            maxWidth: '100%',
                                                            transition: 'all 0.2s ease',
                                                            border: isItemActive ? '1px solid #4085c6' : '1px solid transparent',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', flex: '0 1 auto', minWidth: 0 }}>
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    width: '8px',
                                                                    height: '8px',
                                                                    backgroundColor: categoryColors[item.name] || (isItemActive ? '#4085c6' : '#bfbfbf'),
                                                                    borderRadius: '2px',
                                                                    marginRight: '4px',
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.name}
                                                            </span>
                                                        </div>
                                                        <strong style={{ marginLeft: 2, color: '#434343', flexShrink: 0, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                            {item.value} ({itemPercent.toFixed(2)}%)
                                                        </strong>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                )}
                            </Fragment>
                        );
                    })}
                </ul>
                </div>
            )}
        </div>
    );
});

export default DonutChartWithDetails;





